import { Alert, Button, Card, Checkbox, Col, Form, Input, Spin, Steps, Table } from "antd";
import schemaFieldTemplates from "common/data/schemaFieldTemplates";
import { descriptionRules, nameRules } from "common/FormValidationRules";
import { mergeSuggestionsIntoFields } from "common/applySchemaSuggestions";
import SchemaAutoConfigureOptions, { DEFAULT_SAMPLE_SIZE } from "components/molecules/SchemaAutoConfigureOptions";
import SchemaSuggestionReview from "components/organisms/SchemaSuggestionReview";
import { selectSuggestionCounts } from "store/schemaProfile/reducer";
import { useEffect, useState } from "react";

/**
 * Connection types that can supply a bounded data sample (PRE-2717).
 *
 * Mirrors the capability matrix in
 * system-connection-service/svc/ConnectionSamplerService.js. An S3 connection
 * resolves to objects in a bucket rather than a queryable table, so a file
 * chosen from it belongs to the CSV/TXT path instead.
 */
const SAMPLEABLE_CONNECTION_TYPES = ["jdbc", "snowflake", "salesforce", "hubspot"];

/** Connectors with no "SELECT *": an explicit field list is mandatory. */
const FIELD_LIST_REQUIRED_TYPES = ["salesforce", "hubspot"];


const mapJdbcTypeToPretectum = (jdbcType) => {
    if (!jdbcType) return "string";
    const t = jdbcType.toLowerCase().trim();

    if (["int", "int2", "int4", "int8", "integer", "smallint", "bigint", "tinyint", "serial", "bigserial", "smallserial", "mediumint"].includes(t)) return "integer";

    if (["float", "float4", "float8", "double", "double precision", "real", "numeric", "decimal", "number", "money"].includes(t)) return "float";

    if (["bool", "boolean", "bit"].includes(t)) return "boolean";

    if (["timestamp", "timestamptz", "timestamp without time zone", "timestamp with time zone", "datetime", "datetime2", "smalldatetime"].includes(t)) return "datetime";
    if (["date"].includes(t)) return "date";
    if (["time", "timetz", "time without time zone", "time with time zone"].includes(t)) return "time";

    return "string";
};


const mapSalesforceTypeToPretectum = (sfType) => {
    if (!sfType) return "string";
    const t = sfType.toLowerCase().trim();

    if (["int", "long"].includes(t)) return "integer";
    if (["double", "percent"].includes(t)) return "float";
    if (["currency"].includes(t)) return "currency";
    if (["boolean"].includes(t)) return "boolean";
    if (["date"].includes(t)) return "date";
    if (["datetime"].includes(t)) return "datetime";
    if (["time"].includes(t)) return "time";
    if (["picklist", "multipicklist", "combobox"].includes(t)) return "picklist";
    if (["email"].includes(t)) return "email";
    if (["url"].includes(t)) return "url";
    if (["phone"].includes(t)) return "phone";

    return "string";
};

const mapHubspotTypeToPretectum = (hsType, field = {}) => {
    // HubSpot's property vocabulary does not expose a dedicated "email" type: the
    // standard email property is type:"string", fieldType:"text", and email-ness is
    // enforced by the property NAME. Phone, however, is reliably flagged via
    // fieldType:"phonenumber". So we detect email by name heuristic and phone by
    // fieldType, before falling back to the coarse `type` mapping.
    const fieldType = (field.fieldType || "").toLowerCase().trim();
    const name = (field.name || "").toLowerCase();

    if (fieldType === "phonenumber") return "phone";
    // Name-based email detection: "email" as a bounded token (email, work_email,
    // email_address, hs_additional_emails) - avoids mid-word false hits (e.g. remailer).
    if (name === "email" || /(^|[_-])e[-_]?mails?([_-]|$)/.test(name)) return "email";

    const t = (hsType || "").toLowerCase().trim();

    if (["number"].includes(t)) return "float";
    if (["bool"].includes(t)) return "boolean";
    if (["date"].includes(t)) return "date";
    if (["datetime"].includes(t)) return "datetime";
    if (["enumeration"].includes(t)) return "picklist";
    if (["phone_number"].includes(t)) return "phone";

    return "string";
};

const getTypeMapper = (connectionType) => {
    switch (connectionType) {
        case "jdbc": return mapJdbcTypeToPretectum;
        case "salesforce": return mapSalesforceTypeToPretectum;
        case "hubspot": return mapHubspotTypeToPretectum;
        default: return (t) => t || "string";
    }
};

const getSnowflakeColumnValue = (column, key) => {
    if (!column) {
        return undefined;
    }

    return column[key] ?? column[key.toUpperCase()] ?? column[key.toLowerCase()];
};

const normalizeSnowflakeColumn = (column) => ({
    name: getSnowflakeColumnValue(column, "name"),
    type: getSnowflakeColumnValue(column, "type") || "string",
    nullable: getSnowflakeColumnValue(column, "nullable"),
});

const SchemaModelCreateFromSystemConnection = ({
    systemConnection,
    jdbcConnectionTables,
    getJdbcConnectionTablesResult,
    getJdbcTableSchemaResult,
    jdbcTableSchema,
    snowflakeConnectionTables,
    getSnowflakeConnectionTablesResult,
    getSnowflakeTableSchemaResult,
    snowflakeTableSchema,
    onCreateSchema,
    onGetJdbcConnectionTables,
    onGetJdbcTableSchema,
    onGetSnowflakeConnectionTables,
    onGetSnowflakeTableSchema,
    initialSelectedTable,
    // PRE-2717 / PRE-2722: optional sampling and suggestion review.
    onSampleConnection,
    schemaProfile,
    schemaProfileUnsupported,
    getSchemaProfileResult,
    suggestionStatuses = {},
    onSetSuggestionStatus,
    onSetAllSuggestionStatuses,
    onResetSchemaProfile,
}) => {
    const [form] = Form.useForm();
    const [current, setCurrent] = useState(0);
    const [selectedTable, setSelectedTable] = useState(initialSelectedTable);
    const [fields, setFields] = useState([]);
    const [editingName, setEditingName] = useState(false);
    const [isLoadingSchema, setIsLoadingSchema] = useState(false);
    // Opt-in, default off. Never forced (PRE-2722 AC1).
    const [autoConfigureEnabled, setAutoConfigureEnabled] = useState(false);
    const [sampleSize, setSampleSize] = useState(DEFAULT_SAMPLE_SIZE);
    // Which review rows are visible (PRE-2898). Local to the wizard: a filter
    // is a view preference, not a decision worth persisting in the store.
    const [suggestionStatusFilter, setSuggestionStatusFilter] = useState("ALL");
    const [isSampling, setIsSampling] = useState(false);
    const [showSuggestionReview, setShowSuggestionReview] = useState(false);
    // Surfaced on the Schema Details step when sampling could not run, so a
    // backend failure is never mistaken for "no suggestions found".
    const [samplingError, setSamplingError] = useState(null);

    const connectionType = systemConnection?.connectionType;
    const canAutoConfigure = SAMPLEABLE_CONNECTION_TYPES.includes(connectionType) && !!onSampleConnection;

    const columns = [
        {
            title: "Table Name",
            dataIndex: "Name",
            key: "Name",
        },
    ];

    const fieldColumns = [
        {
            title: "Field Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
        },
        {
            title: "Required",
            dataIndex: "isRequired",
            key: "isRequired",
            render: (isRequired) => (isRequired ? "Yes" : "No"),
        },
        {
            title: "Primary Key",
            dataIndex: "isPrimaryKey",
            key: "isPrimaryKey",
            render: (isPrimaryKey) => (isPrimaryKey ? "Yes" : "No"),
        },
    ];

    useEffect(() => {
        if (systemConnection) {
            if (systemConnection.connectionType === "jdbc") {
                onGetJdbcConnectionTables(systemConnection.connectionId);
            } else if (systemConnection.connectionType === "snowflake") {
                onGetSnowflakeConnectionTables(systemConnection.connectionId);
            }
        }
    }, [onGetJdbcConnectionTables, onGetSnowflakeConnectionTables, systemConnection]);

    useEffect(() => {
        form.setFieldsValue({
            schemaName: "",
            schemaDescription: "",
        });
    }, [form]);

    // Load the selected table's fields.
    //
    // This deliberately does NOT depend on `current`. It used to, and the
    // result was that every step change re-ran this effect; on the JDBC/
    // Snowflake path (no pre-supplied fields) that re-dispatched the table
    // schema fetch, whose success handler calls setFields(...) with the plain
    // connector-derived fields. On the way into Schema Details that silently
    // clobbered the fields the auto-configure merge step had just enriched, so
    // every accepted suggestion was discarded before create (PRE-2716 /
    // PRE-2722). Keying the fetch off the table alone fixes it; the
    // "auto-advance to Review Fields" concern moved to its own effect below.
    useEffect(() => {
        if (!selectedTable) return;
        // Check if we already have fields (e.g., from Salesforce/HubSpot connection selector)
        if (initialSelectedTable?.fields && initialSelectedTable.fields.length > 0) {
            const mapType = getTypeMapper(systemConnection.connectionType);
            setFields(
                initialSelectedTable.fields.map((f) => ({
                    name: f.name,
                    label: f.label || f.name,
                    type: mapType(f.type, f),
                    checked: true,
                    isPrimaryKey: f.isPrimaryKey || false,
                    isRequired: f.isRequired || false,
                    description: f.label || f.name,
                })),
            );
            setIsLoadingSchema(false);
        } else if (initialSelectedTable?.data && initialSelectedTable?.columns) {
            setFields(
                initialSelectedTable.columns.map((col) => ({
                    name: col,
                    type: "string",
                    checked: true,
                    isPrimaryKey: false,
                    isRequired: false,
                    description: col,
                })),
            );
            setIsLoadingSchema(false);
        } else {
            setIsLoadingSchema(true);
            const tableName = typeof selectedTable === "string" ? selectedTable : selectedTable.Name;
            if (systemConnection.connectionType === "jdbc") {
                onGetJdbcTableSchema(systemConnection.connectionId, tableName);
            } else if (systemConnection.connectionType === "snowflake") {
                onGetSnowflakeTableSchema(systemConnection.connectionId, tableName);
            }
        }
    }, [selectedTable, initialSelectedTable, onGetJdbcTableSchema, onGetSnowflakeTableSchema, systemConnection]);

    // Auto-advance from Select Table to Review Fields when the wizard was
    // entered with a pre-selected table (the Salesforce/HubSpot selector path).
    // Kept separate from the fetch effect so a step change never refetches.
    useEffect(() => {
        if (initialSelectedTable && selectedTable && current === 0) {
            setCurrent(1);
        }
    }, [initialSelectedTable, selectedTable, current]);

    // When schema is fetched, update local state
    useEffect(() => {
        if (jdbcTableSchema && jdbcTableSchema.data && jdbcTableSchema.data.data && jdbcTableSchema.data.data.length > 0) {
            setFields(
                jdbcTableSchema.data.data.map((col) => ({
                    name: col.name,
                    type: mapJdbcTypeToPretectum(col.type),
                    checked: true,
                    isPrimaryKey: false,
                    isRequired: col.nullable === "NO",
                    description: col.name,
                })),
            );
            setIsLoadingSchema(false);
        }
    }, [jdbcTableSchema]);

    useEffect(() => {
        if (snowflakeTableSchema && snowflakeTableSchema.length > 0) {
            setFields(
                snowflakeTableSchema.map((col) => {
                    const normalizedColumn = normalizeSnowflakeColumn(col);

                    return {
                        name: normalizedColumn.name,
                        type: normalizedColumn.type,
                        checked: true,
                        isPrimaryKey: false,
                        isRequired: normalizedColumn.nullable === false || normalizedColumn.nullable === "NO",
                        description: normalizedColumn.name,
                    };
                }),
            );
            setIsLoadingSchema(false);
        }
    }, [snowflakeTableSchema]);

    // Handle schema fetch error
    useEffect(() => {
        if (getJdbcTableSchemaResult && !getJdbcTableSchemaResult.success && getJdbcTableSchemaResult.message) {
            setIsLoadingSchema(false);
        }
        if (getSnowflakeTableSchemaResult && !getSnowflakeTableSchemaResult.success && getSnowflakeTableSchemaResult.message) {
            setIsLoadingSchema(false);
        }
    }, [getJdbcTableSchemaResult, getSnowflakeTableSchemaResult]);

    const next = () => setCurrent(current + 1);
    const prev = () => setCurrent(current - 1);

    /**
     * Leaving "Review Fields": sample and profile if the user opted in,
     * otherwise go straight to schema details exactly as before.
     */
    const onFieldsReviewNext = () => {
        if (!autoConfigureEnabled || !canAutoConfigure) {
            setCurrent(2);
            return;
        }

        const tableName = typeof selectedTable === "string" ? selectedTable : selectedTable?.Name;
        const selectedFieldNames = fields.filter((field) => field.checked).map((field) => field.name);

        // Salesforce and HubSpot have no "SELECT *", so sampling without a
        // field list would fail server-side. Skip rather than round-trip.
        if (FIELD_LIST_REQUIRED_TYPES.includes(connectionType) && selectedFieldNames.length === 0) {
            setCurrent(2);
            return;
        }

        setIsSampling(true);
        onSampleConnection({
            connectionId: systemConnection.connectionId,
            connectionType,
            objectName: tableName,
            fields: selectedFieldNames,
            sampleSize,
        });
    };

    /** A proposal, or a refusal to sample, came back. Show the review step. */
    useEffect(() => {
        if (!isSampling) return;
        if (!schemaProfile && !schemaProfileUnsupported) return;
        setIsSampling(false);
        setShowSuggestionReview(true);
    }, [isSampling, schemaProfile, schemaProfileUnsupported]);

    /**
     * Sampling failed outright. Continue unassisted rather than blocking, but
     * say so: silently advancing leaves the user unable to tell a backend
     * failure from a genuine "no suggestions found".
     */
    useEffect(() => {
        if (!isSampling) return;
        if (getSchemaProfileResult && getSchemaProfileResult.success === false) {
            setIsSampling(false);
            setSamplingError(
                getSchemaProfileResult.message ||
                    "Could not sample this connection. Continuing without auto-configure.",
            );
            setCurrent(2);
        }
    }, [isSampling, getSchemaProfileResult]);

    /**
     * Apply accepted suggestions onto the connector-derived fields.
     *
     * Deliberately a merge, not a replace: the connector already reported real
     * types, and the proposal's baseline is always `string`, so replacing would
     * downgrade the schema. With nothing accepted the fields are untouched.
     */
    const onSuggestionReviewContinue = () => {
        const { fields: mergedFields } = mergeSuggestionsIntoFields(fields, schemaProfile, suggestionStatuses);
        setFields(mergedFields);
        setShowSuggestionReview(false);
        onResetSchemaProfile && onResetSchemaProfile();
        setCurrent(2);
    };

    const onSuggestionReviewCancel = () => {
        setShowSuggestionReview(false);
        onResetSchemaProfile && onResetSchemaProfile();
    };

    // Table selection UI
    const renderTableList = () => {
        const isSnowflake = systemConnection.connectionType === "snowflake";
        const tablesResult = isSnowflake ? getSnowflakeConnectionTablesResult : getJdbcConnectionTablesResult;
        const tables = isSnowflake ? snowflakeConnectionTables : jdbcConnectionTables;

        if (!tablesResult.success) {
            return <div style={{ padding: 32 }}>Failed to load tables.</div>;
        }
        if (!tables || tables.length === 0) {
            return <div style={{ padding: 32 }}>No tables found.</div>;
        }
        return (
            <Card title="Select Table" style={{ minWidth: 250, maxWidth: 300 }}>
                <Table
                    size="small"
                    rowKey="Name"
                    dataSource={tables}
                    pagination={false}
                    onRow={(record) => ({
                        onClick: () => setSelectedTable(record),
                    })}
                    rowClassName={(record) => (selectedTable && selectedTable.Name === record.Name ? "ant-table-row-selected" : "")}
                >
                    <Table.Column title="Table Name" dataIndex="Name" key="Name" />
                    <Table.Column title="Type" dataIndex="Type" key="Type" />
                </Table>
            </Card>
        );
    };

    // Field selection UI
    const renderFields = () => (
        <Card title={selectedTable ? selectedTable.Name : "Fields"} style={{ minWidth: 250, maxWidth: 300, margin: "0 16px" }}>
            <div style={{ marginBottom: 8 }}>
                <span style={{ color: "#888" }}>Select the fields to include in your schema.</span>
            </div>
            {isLoadingSchema ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "20px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                fields.map((field, idx) => (
                    <div key={field.name} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ width: 24, display: "inline-block", textAlign: "center", fontWeight: "bold" }}>{field.type}</span>
                        <span style={{ flex: 1, marginLeft: 8 }}>{field.name}</span>
                        <Checkbox
                            checked={field.checked}
                            onChange={(e) => {
                                const newFields = [...fields];
                                newFields[idx].checked = e.target.checked;
                                setFields(newFields);
                            }}
                        />
                    </div>
                ))
            )}
        </Card>
    );

    // Schema editor UI
    const renderSchemaEditor = () => (
        <Card
            title={
                editingName ? (
                    <Form.Item name="schemaName" rules={nameRules} style={{ marginBottom: 0 }}>
                        <Input onBlur={() => setEditingName(false)} onPressEnter={() => setEditingName(false)} autoFocus style={{ maxWidth: 200 }} />
                    </Form.Item>
                ) : (
                    <span onClick={() => setEditingName(true)} style={{ cursor: "pointer" }}>
                        {form.getFieldValue("schemaName") || "Enter schema name"} <span style={{ color: "#888", fontSize: 14 }}>✎</span>
                    </span>
                )
            }
            style={{ minWidth: 250, maxWidth: 300 }}
        >
            <div style={{ marginBottom: 8 }}>
                <Form.Item name="schemaDescription" rules={descriptionRules} style={{ marginBottom: 8 }}>
                    <Input.TextArea placeholder="Enter schema description" autoSize />
                </Form.Item>
            </div>
            {fields
                .filter((f) => f.checked)
                .map((field) => (
                    <div key={field.name} style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ width: 24, display: "inline-block", textAlign: "center", fontWeight: "bold" }}>{field.type}</span>
                        <span style={{ flex: 1, marginLeft: 8 }}>{field.name}</span>
                        <Checkbox checked readOnly />
                    </div>
                ))}
        </Card>
    );

    // Stepper content
    let content;
    if (current === 0) {
        const isSnowflake = systemConnection.connectionType === "snowflake";
        const tablesResult = isSnowflake ? getSnowflakeConnectionTablesResult : getJdbcConnectionTablesResult;
        const tables = isSnowflake ? snowflakeConnectionTables : jdbcConnectionTables;
        content = <Col flex="1 1 0%">{tablesResult && tablesResult.success && !tables.length ? <Spin /> : renderTableList()}</Col>;
    } else if (current === 1) {
        content = (
            <>
                <Col flex="1 1 0%">{renderTableList()}</Col>
                <Col flex="1 1 0%">{renderFields()}</Col>
            </>
        );
    } else if (current === 2) {
        content = (
            <>
                <Col flex="1 1 0%">{renderTableList()}</Col>
                <Col flex="1 1 0%">{renderFields()}</Col>
                <Col flex="1 1 0%">{renderSchemaEditor()}</Col>
            </>
        );
    }

    const handleFinish = async () => {
        try {
            // Validate form before submission
            const values = await form.validateFields();

            const selectedFields = fields
                .filter((field) => field.checked)
                .map((field) => {
                    const template = schemaFieldTemplates[field.type] || schemaFieldTemplates.string;
                    const mapped = {
                        isPrimaryKey: field.isPrimaryKey || false,
                        isSortKey: false,
                        active: true,
                        dataType: field.type || "string",
                        isRequired: field.isRequired || false,
                        min: field.min || template.min || 1,
                        max: field.max || template.max || 500,
                        isMultivalue: false,
                        isPII: false,
                        name: field.name,
                        description: field.description || field.name,
                        maxSize: template.maxSize || 0,
                        maxWidth: template.maxWidth || 0,
                        maxHeight: template.maxHeight || 0,
                    };
                    // Carry constraints that accepted suggestions enriched onto
                    // the field (DATE_FORMAT, boolean style). Without this the
                    // merge sets field.dateFormat but the create payload drops
                    // it, so a date-format suggestion silently has no effect.
                    // Values are already vocabulary-checked by
                    // applySchemaSuggestions, so nothing illegal reaches the API.
                    if (field.dateFormat) mapped.dateFormat = field.dateFormat;
                    if (field.dateSeparator) mapped.dateSeparator = field.dateSeparator;
                    if (field.timeFormat) mapped.timeFormat = field.timeFormat;
                    if (field.allowedBooleanValues) mapped.allowedBooleanValues = field.allowedBooleanValues;
                    return mapped;
                });

            const schemaData = {
                name: values.schemaName,
                description: values.schemaDescription,
                active: true,
                fields: selectedFields,
                table: selectedTable,
                connection: systemConnection,
                businessAreaId: systemConnection.businessAreaId,
            };

            onCreateSchema(schemaData);
        } catch (errorInfo) {
            console.log("Form validation failed:", errorInfo);
        }
    };

    // The suggestion review takes over the surface while it is open, so the
    // user reviews one thing at a time rather than a stepper plus a proposal.
    if (showSuggestionReview) {
        return (
            <div className="p-4">
                <h2 className="mb-4 text-xl font-bold">Review Suggestions</h2>
                <SchemaSuggestionReview
                    schemaProfile={schemaProfile}
                    suggestionStatuses={suggestionStatuses}
                    counts={selectSuggestionCounts(schemaProfile, suggestionStatuses)}
                    unsupported={schemaProfileUnsupported}
                    statusFilter={suggestionStatusFilter}
                    onStatusFilterChange={setSuggestionStatusFilter}
                    onSetSuggestionStatus={onSetSuggestionStatus}
                    onSetAllSuggestionStatuses={onSetAllSuggestionStatuses}
                    onContinue={onSuggestionReviewContinue}
                    onCancel={onSuggestionReviewCancel}
                />
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="mb-4">
                <Steps
                    current={current}
                    className="mb-8"
                    items={[
                        {
                            title: "Select Table",
                        },
                        {
                            title: "Review Fields",
                        },
                        {
                            title: "Schema Details",
                        },
                    ]}
                />
            </div>
            {current === 0 && (
                <div>
                    <h2 className="mb-4 text-xl font-bold">Select a Table</h2>
                    {isLoadingSchema ? (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={systemConnection.connectionType === "snowflake" ? snowflakeConnectionTables : jdbcConnectionTables}
                            loading={(systemConnection.connectionType === "snowflake" ? getSnowflakeConnectionTablesResult : getJdbcConnectionTablesResult).loading}
                            rowKey="Name"
                            onRow={(record) => ({
                                onClick: () => setSelectedTable(record),
                            })}
                            className="mb-4"
                        />
                    )}
                </div>
            )}
            {current === 1 && (
                <div>
                    <h2 className="mb-4 text-xl font-bold">Review Fields</h2>
                    {isLoadingSchema ? (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <>
                            {/*
                             * Above the table, not below it (PRE-2898). On a
                             * wide schema the table plus its pagination pushes
                             * anything underneath off-screen, so the one
                             * control that turns the whole feature on was
                             * invisible unless the user happened to scroll.
                             */}
                            {canAutoConfigure && (
                                <div className="mb-4 rounded-md bg-slate-50 p-3">
                                    <SchemaAutoConfigureOptions
                                        enabled={autoConfigureEnabled}
                                        sampleSize={sampleSize}
                                        onEnabledChange={setAutoConfigureEnabled}
                                        onSampleSizeChange={setSampleSize}
                                    />
                                </div>
                            )}
                            <Table columns={fieldColumns} dataSource={fields} rowKey="name" className="mb-4" />
                            <div className="flex justify-end">
                                <Button onClick={() => setCurrent(0)} className="mr-2" disabled={isSampling}>
                                    Back
                                </Button>
                                <Button type="primary" onClick={onFieldsReviewNext} loading={isSampling}>
                                    {isSampling ? `Sampling ${sampleSize} row(s)...` : "Next"}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
            {current === 2 && (
                <div>
                    <h2 className="mb-4 text-xl font-bold">Schema Details</h2>
                    {samplingError && (
                        <Alert
                            type="warning"
                            showIcon
                            closable
                            className="mb-4"
                            message="Auto-configure did not run"
                            description={samplingError}
                            onClose={() => setSamplingError(null)}
                        />
                    )}
                    <Form form={form} layout="vertical">
                        <div className="mb-4">
                            <Form.Item label="Schema Name" name="schemaName" rules={nameRules}>
                                {editingName ? (
                                    <Input placeholder="Enter schema name" onBlur={() => setEditingName(false)} autoFocus className="w-full" />
                                ) : (
                                    <div onClick={() => setEditingName(true)} className="cursor-pointer rounded border border-gray-300 p-2">
                                        {form.getFieldValue("schemaName") || "Enter schema name"}
                                    </div>
                                )}
                            </Form.Item>
                        </div>
                        <div className="mb-4">
                            <Form.Item label="Schema Description" name="schemaDescription" rules={descriptionRules}>
                                <Input.TextArea placeholder="Enter schema description" rows={4} className="w-full" />
                            </Form.Item>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={() => setCurrent(1)} className="mr-2">
                                Back
                            </Button>
                            <Button type="primary" onClick={handleFinish}>
                                Create Schema
                            </Button>
                        </div>
                    </Form>
                </div>
            )}
        </div>
    );
};

export default SchemaModelCreateFromSystemConnection;
